import { createFileRoute } from '@tanstack/react-router'
import { MarketingPage } from '../components/MarketingPage.js'

export const Route = createFileRoute('/terms')({
  component: TermsPage,
})

function TermsPage() {
  return (
    <MarketingPage
      eyebrow="Legal"
      title="Terms of service"
      effectiveDate="17 May 2026"
    >
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the
        Brightstars software platform, including the websites located at
        brightstars.in and academiaportal.com, and any related services
        (collectively, the &ldquo;Service&rdquo;) operated by Brightstars
        (&ldquo;Brightstars&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
        &ldquo;our&rdquo;).
      </p>
      <p>
        By creating an account, subscribing to a plan, or otherwise using the
        Service, you agree to be bound by these Terms. If you do not agree,
        please do not use the Service.
      </p>

      <h2>1. Who can use the Service</h2>
      <p>
        The Service is intended for coaching academies, tuition centres, and
        other educational businesses operating in India, and for the parents,
        students, and teachers associated with them. You must be at least 18
        years old and competent to enter into a contract under the Indian
        Contract Act, 1872 to register an academy account.
      </p>

      <h2>2. Accounts and registration</h2>
      <p>
        Academy administrators are responsible for the accuracy of the
        information they provide during registration, for keeping their
        credentials secure, and for all activity that occurs under their
        account. You must notify us immediately at{' '}
        <a href="mailto:hello@brightstars.in">hello@brightstars.in</a> of any
        unauthorised access.
      </p>

      <h2>3. Subscriptions, fees, and taxes</h2>
      <p>
        Brightstars is provided on a subscription basis. The pricing for each
        plan is shown on our <a href="/#pricing">pricing page</a> in Indian
        Rupees (INR). Goods and Services Tax (GST) is charged at the
        applicable rate (currently 18%) in addition to the displayed plan
        price and will appear as a separate line on your invoice.
      </p>
      <p>
        Subscription fees are billed in advance through Razorpay Subscriptions
        on a monthly or annual cycle, depending on the plan you choose. By
        subscribing, you authorise us to charge the payment method on file
        for each renewal until you cancel.
      </p>
      <p>
        Free trials, where offered, automatically convert into paid
        subscriptions at the end of the trial period unless cancelled. You
        will not be charged during the trial.
      </p>

      <h2>4. Parent fee collection</h2>
      <p>
        The Service includes tools that allow academies to collect tuition
        fees from parents via Razorpay. Brightstars acts only as a technology
        facilitator. Money flows directly to the academy&apos;s designated
        bank account. Academies are solely responsible for the fee amounts
        they raise, for issuing receipts, for tax compliance on those fees,
        and for refunds to parents.
      </p>

      <h2>5. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use the Service for any unlawful purpose or in violation of any
          applicable law, including data-protection, anti-spam, and
          messaging-platform policies (e.g. the WhatsApp Business Messaging
          Policy).
        </li>
        <li>
          Send spam or unsolicited communications to parents, students, or
          third parties.
        </li>
        <li>
          Upload viruses, malware, or other harmful code, or attempt to
          breach the security of the Service.
        </li>
        <li>
          Reverse-engineer, decompile, scrape, or resell the Service without
          our written permission.
        </li>
        <li>
          Use the Service to harvest, harass, or otherwise harm minors.
        </li>
      </ul>

      <h2>6. Your content</h2>
      <p>
        You retain ownership of the data and content you upload (student
        records, marks, attendance, branding assets, etc.) (&ldquo;Customer
        Data&rdquo;). You grant Brightstars a worldwide, non-exclusive,
        royalty-free licence to host, process, and transmit Customer Data
        solely as needed to provide the Service. You are responsible for
        obtaining all necessary consents (from parents, guardians, and
        teachers) before uploading personal information.
      </p>

      <h2>7. Intellectual property</h2>
      <p>
        Brightstars and its licensors retain all rights, title, and interest
        in and to the Service, including the software, design, trademarks,
        and documentation. Nothing in these Terms transfers ownership of
        Brightstars intellectual property to you.
      </p>

      <h2>8. Third-party services</h2>
      <p>
        The Service integrates with third-party providers for payments, cloud
        hosting, WhatsApp messaging, and email. Your use of those features is
        also subject to the relevant provider&apos;s terms and privacy
        policies.
      </p>

      <h2>9. Suspension and termination</h2>
      <p>
        You may cancel your subscription at any time from the billing screen
        in your dashboard. We may suspend or terminate your account, with or
        without notice, if you breach these Terms, fail to pay subscription
        fees, or use the Service in a way that risks harm to us or to other
        users.
      </p>
      <p>
        On termination, your access to the Service ends. We will retain your
        Customer Data for 30 days to allow export, after which it may be
        permanently deleted.
      </p>

      <h2>10. Disclaimer of warranties</h2>
      <p>
        The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo; basis. To the maximum extent permitted by law,
        Brightstars disclaims all warranties, express or implied, including
        merchantability, fitness for a particular purpose, and
        non-infringement.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Brightstars&apos; total
        aggregate liability arising out of or relating to the Service shall
        not exceed the subscription fees paid by you to Brightstars in the
        12 months preceding the claim. Brightstars shall not be liable for
        indirect, incidental, special, consequential, or punitive damages,
        including loss of profits, data, or goodwill.
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You agree to indemnify and hold harmless Brightstars from any claim,
        loss, or liability (including legal fees) arising out of your breach
        of these Terms, your Customer Data, or your use of the Service in
        violation of applicable law.
      </p>

      <h2>13. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of India. Subject to mandatory
        consumer protections, the courts at Hyderabad, Telangana shall have
        exclusive jurisdiction over any dispute arising out of or relating to
        these Terms or the Service.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be
        notified by email to the address on file and posted on this page with
        an updated effective date. Continued use of the Service after the
        effective date constitutes acceptance of the revised Terms.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms can be sent to{' '}
        <a href="mailto:hello@brightstars.in">hello@brightstars.in</a> or via
        our <a href="/contact">contact page</a>.
      </p>
    </MarketingPage>
  )
}
